// AdminStateImages — admin uploads a state-branded blog image (state outline
// + PMW logo, Michigan-style) for each of the 50 U.S. states. Auto-blog picks
// the image up when generating dealer-recruitment posts; states without an
// uploaded image fall back to the plain PMW logo.
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, Trash2, Image as ImageIcon, Link as LinkIcon, Search, X } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function absolutize(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function AdminStateImages() {
  return <StateImagesPanel />;
}

export function StateImagesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [busyCode, setBusyCode] = useState(null);
  const [urlEdit, setUrlEdit] = useState({});
  const fileInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/state-images`);
      setRows(data);
    } catch (e) { toast.error("Failed to load state images"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (code, file) => {
    if (!file) return;
    setBusyCode(code);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_URL}/admin/state-images/${code}`, fd, {
        headers: { ...authHeader() },
      });
      toast.success(`${code} image uploaded`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusyCode(null); }
  };

  const setUrl = async (code) => {
    const image_url = (urlEdit[code] || "").trim();
    if (!image_url) return toast.error("Paste an image URL first");
    setBusyCode(code);
    try {
      await axios.post(`${API_URL}/admin/state-images/${code}/set-url`, { image_url }, {
        headers: { ...authHeader(), "Content-Type": "application/json" },
      });
      toast.success(`${code} URL saved`);
      setUrlEdit({ ...urlEdit, [code]: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusyCode(null); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Remove ${code} state image? Blogs will fall back to the plain PMW logo.`)) return;
    setBusyCode(code);
    try {
      await axios.delete(`${API_URL}/admin/state-images/${code}`, { headers: authHeader() });
      toast.success(`${code} image removed`);
      await load();
    } catch (e) { toast.error("Delete failed"); }
    finally { setBusyCode(null); }
  };

  const uploadedCount = rows.filter((r) => r.image_url).length;
  const filtered = rows.filter((r) => {
    if (!searchQ.trim()) return true;
    const q = searchQ.trim().toLowerCase();
    return r.state_name.toLowerCase().includes(q) || r.state_code.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-navy">
              <ImageIcon className="w-5 h-5 text-teal" /> State Blog Images
              <span className="text-xs font-normal text-slate-500 ml-2">
                {uploadedCount}/50 uploaded
              </span>
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1 font-body">
              Upload a state-branded map (like the Michigan example — outline + PMW logo) for each state. Dealer-recruitment blogs auto-pick the right image by state; states without an uploaded image fall back to the plain PMW logo.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search state name or code…"
              className="pl-9 pr-9"
              data-testid="state-image-search"
            />
            {searchQ && (
              <button
                type="button"
                onClick={() => setSearchQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-slate-500 text-center py-8 font-body">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map((s) => {
                const hasImg = !!s.image_url;
                const busy = busyCode === s.state_code;
                return (
                  <div
                    key={s.state_code}
                    className={`p-3 rounded-lg border ${hasImg ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"} flex gap-3 items-center`}
                    data-testid={`state-image-row-${s.state_code}`}
                  >
                    <div className="w-16 h-16 rounded-md bg-white border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {hasImg ? (
                        <img src={absolutize(s.image_url)} alt={s.state_name} className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-navy text-sm truncate">
                          {s.state_name} <span className="text-slate-400">({s.state_code})</span>
                        </p>
                        {hasImg && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Uploaded</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <input
                          ref={(el) => (fileInputs.current[s.state_code] = el)}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => upload(s.state_code, e.target.files?.[0])}
                          data-testid={`state-image-file-${s.state_code}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => fileInputs.current[s.state_code]?.click()}
                          className="text-xs h-7"
                          data-testid={`state-image-upload-btn-${s.state_code}`}
                        >
                          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                          {hasImg ? "Replace" : "Upload"}
                        </Button>
                        <details className="inline-flex">
                          <summary className="text-xs text-slate-500 hover:text-navy cursor-pointer px-2 py-1 rounded border border-slate-200 flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" /> URL
                          </summary>
                          <div className="mt-2 flex gap-1 items-center">
                            <Input
                              value={urlEdit[s.state_code] || ""}
                              onChange={(e) => setUrlEdit({ ...urlEdit, [s.state_code]: e.target.value })}
                              placeholder="https://…"
                              className="h-7 text-xs w-56"
                            />
                            <Button size="sm" className="h-7 text-xs bg-navy hover:bg-navy-dark text-white" onClick={() => setUrl(s.state_code)} disabled={busy}>Save</Button>
                          </div>
                        </details>
                        {hasImg && (
                          <Button size="sm" variant="ghost" onClick={() => remove(s.state_code)} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs h-7" data-testid={`state-image-remove-${s.state_code}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 p-4 rounded-lg bg-slate-50 text-xs font-body text-slate-600 border border-slate-200">
            <p><strong>Tip:</strong> After uploading or changing a state image, click <em>Locations → Cities → "Generate Dealer Recruitment Blogs"</em> to auto-refresh any existing dealer-recruitment blogs for that state. The generator detects image drift and regenerates the affected posts.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
